import axios from 'axios';
import { getAppUrl } from 'utils/dynamicSettings';
import { getIdToken, getRefreshToken } from 'utils/token';
import { getSessionIdFromSessionStorage } from 'utils/url';
import { BaseHttpClient } from '../BaseHttpClient';
import { injectCommonInterceptors } from '../interceptors';
import {
  AuthConfigResponse,
  GetUserInfoResponse,
  InitLoginRequest,
  InitLoginResponse,
} from './api.schema';
import { BASE_URL } from './consts';

const axiosOIDCAuthenticationInstance = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
});

injectCommonInterceptors(axiosOIDCAuthenticationInstance);

export class OIDCAuthenticationClient extends BaseHttpClient {
  constructor() {
    super(axiosOIDCAuthenticationInstance);
  }

  // Singleton instance for access without instantiation
  static override instance = new OIDCAuthenticationClient();

  static async getUserInfo() {
    return await OIDCAuthenticationClient.get<GetUserInfoResponse>('/userinfo');
  }

  static async endSession() {
    const idToken = getIdToken();
    const refreshToken = getRefreshToken();

    // With neither credential the server has nothing to revoke — skip the
    // network request and let the caller fall back to local cleanup/redirect.
    // Either one alone is worth sending: id_token_hint drives Firebase
    // revocation, refresh_token drives OIDC grant revocation.
    if (!idToken && !refreshToken) {
      return;
    }

    // POST with the credentials in the body — refresh_token and id_token_hint
    // must never ride in a URL, where they end up in access logs and telemetry.
    return await OIDCAuthenticationClient.post<void>('/end_session', {
      id_token_hint: idToken,
      refresh_token: refreshToken,
      session_id: getSessionIdFromSessionStorage(),
      post_logout_redirect_uri: `${getAppUrl()}/signin`,
    });
  }

  /**
   * Confirm a CLI device-login code so the waiting CLI's poll releases credentials.
   * @param sessionId - The login session id the CLI is polling against.
   * @param userCode - The device code shown in the terminal, normalized to `XXXX-XXXX`.
   * @returns The `/device/confirm` response payload (`{ status }`).
   */
  static async confirmDeviceCode(sessionId: string, userCode: string) {
    return await OIDCAuthenticationClient.post<{ status: string }>(
      '/device/confirm',
      { session_id: sessionId, user_code: userCode },
      { extractNestedData: false },
    );
  }

  /**
   * Start a login session on this deployment. Used on the ST landing leg of a CLI
   * login to open an ST-local device session that adopts the MT-issued `user_code`
   * (`adoptUserCode`), so the CLI's poll-by-code resolves to this ST session.
   * @param request - trace_id, init_client, and optional adopt_user_code.
   * @returns The `/init_login` response; callers use `session_id` for the confirm step.
   */
  static async initLogin(request: InitLoginRequest) {
    return await OIDCAuthenticationClient.post<InitLoginResponse>(
      '/init_login',
      request,
      { extractNestedData: false },
    );
  }

  static async getAuthConfig() {
    return await OIDCAuthenticationClient.get<AuthConfigResponse>('/config');
  }
}
